<template>
  <Portal>
    <div v-if="show" class="modal-component">
      <transition name="fade-fast">
        <Overlay v-if="show" @click.stop="cancelHandler" />
      </transition>

      <transition name="fade-translatey" appear>
        <div v-if="show" class="modal-component--content">
          <div
            class="modal-component--flex"
            :style="{ width: width, 'min-width': minWidth }"
          >
            <!-- HEADER -->
            <section
              class="modal-component--header"
              :class="{
                hasSubtitle: subtitle,
                'modal-component--header__small': smallHeader
              }"
            >
              <slot name="modal-header">
                <Heading level="1">{{ title }}</Heading>
                <Paragraph v-if="subtitle">{{ subtitle }}</Paragraph>
              </slot>

              <button
                @click="cancelHandler"
                class="modal-component--close-button"
              >
                <Icon icon="x-icon" />
              </button>
            </section>

            <!-- Espaço para colocar alertas na modal
            @see https://www.figma.com/file/9AFe6i7bVhMup1bYeppCU7/Tarifas?node-id=1%3A3016 -->
            <transition name="fade">
              <slot name="warning">
                <div v-if="warning" class="modal-component--warning">
                  <Icon icon="alert" class="alert-icon" />
                  <p class="warning__highlight">
                    Atenção!
                  </p>
                  <p v-html="warning" class="warning--text"></p>
                </div>
              </slot>
            </transition>

            <!-- CONTEÚDO DA MODAL -->
            <section class="modal-component--body">
              <slot name="body-content"> </slot>
            </section>

            <!-- RODAPÉ -->
            <section v-if="hasFooter" class="modal-component--footer">
              <SeparateLine :left="0" :right="0" :bottom="0" :top="0" />

              <div class="flex-buttons">
                <slot name="actions">
                  <!-- CANCELAR -->
                  <Button
                    class="modal-component--content-button"
                    size="large"
                    layout="outline"
                    @click="cancelHandler"
                    >{{ cancelButtonText }}</Button
                  >

                  <!-- CONFIRMAÇÃO -->
                  <Button
                    class="modal-component--content-button"
                    size="large"
                    @click="confirmHandler"
                    >{{ confirmButtonText }}</Button
                  >
                </slot>
              </div>
            </section>
          </div>
        </div>
      </transition>
    </div>
  </Portal>
</template>

<script>
/**
 * Componente de estrutura para para modais
 *
 * @event modal-confirm disparado quando o botão de confirmação é clicado
 * @event modal-cancel disparado quando o botão de cancelar é clicado
 */
import { Portal } from '@linusborg/vue-simple-portal' /** https://github.com/LinusBorg/vue-simple-portal */
import Heading from '../elements/Heading'
import Overlay from '../elements/Overlay'
import Button from '../elements/Button'
import Paragraph from '../elements/Paragraph.vue'
import SeparateLine from '../patterns/SeparateLine.vue'
import { lockBodyScroll, unlockBodyScroll } from '@/helpers/dom'

export default {
  name: 'Modal',
  components: {
    Overlay,
    Heading,
    Paragraph,
    Button,
    SeparateLine,
    Portal
  },
  props: {
    /** Estado da renderização */
    value: {
      type: Boolean,
      default: false
    },
    /** Título */
    title: {
      type: String,
      default: 'Pronto para adicionar ao calendário?'
    },
    /**
     * Subtítulo
     */
    subtitle: {
      type: String,
      default: ''
    },
    /** Texto do botão de confirmação */
    confirmButtonText: {
      type: String,
      default: 'Aplicar'
    },
    /** Texto do botão de cancelar */
    cancelButtonText: {
      type: String,
      default: 'Cancelar'
    },
    /** Se terá ou não um rodapé */
    hasFooter: {
      type: Boolean,
      default: true
    },
    /**
     * Define uma largura para a modal
     * @type String
     * @example width="600px"
     */
    width: {
      type: String,
      default: '748px'
    },
    /**
     * Define uma largura minima para a modal
     * @type String
     * @example width="600px"
     */
    minWidth: {
      type: String,
      default: '0px'
    },
    /** Se deve apresentar o header reduzido */
    smallHeader: {
      type: Boolean,
      default: false
    },
    /**
     * Texto de alerta
     * @see https://www.figma.com/file/9AFe6i7bVhMup1bYeppCU7/Tarifas?node-id=1%3A3016
     */
    warning: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      resolve: null /** Callback de resolução da promise */,
      reject: null /** Callback de rejeição da promise */
    }
  },
  computed: {
    show() {
      return this.value
    }
  },
  watch: {
    /**
     * Observa o estado da renderização para
     * habilitar ou desabilitar o scroll do body
     */
    value(newValue) {
      if (newValue) {
        /** Desabilita o scroll do body */
        lockBodyScroll()
      } else {
        /** Habilita o scroll do body */
        unlockBodyScroll()
      }
    }
  },
  methods: {
    /**
     * Abre a modal e cria uma promise que é resolvida ao
     * confirmar, negar ou fechar a modal
     *
     * @example
     * this.$refs.dialogModal.openModal().then(response => ...).catch(err => ...)
     */
    openModal() {
      return new Promise((resolve, reject) => {
        this.resolve = resolve
        this.reject = reject

        /** Mostra a modal */
        this.$emit('input', true)
      })
    },
    confirmHandler() {
      this.$emit('input', false)
      this.$emit('modal-confirm')

      /** Se existe uma promise para resolver */
      if (this.resolve) {
        this.resolve(true)
      }
    },
    cancelHandler() {
      this.$emit('input', false)
      this.$emit('modal-cancel')

      /** Se existe uma promise para rejeitar */
      if (this.reject) {
        this.reject(true)
      }
    }
  }
}
</script>
<style lang="scss" scoped>
$border-color: $gray-border;

.modal-component {
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  width: 100vw;
  height: 100vh;
  top: 0;
  left: 0;
  z-index: $modal-z-index;

  &--content {
    position: absolute;
    max-height: 90%;
    overflow-y: auto;
    @include scrollbars(5px, $border-color, #fff);

    color: $gray-darkest;
    @include drop-shadow(0px 20px 40px rgba(0, 0, 0, 0.15));

    &-button {
      width: 160px;

      & + & {
        margin-left: 12px;
      }
    }
  }

  &--flex {
    background-color: $white;
  }

  &--warning {
    display: flex;
    align-items: center;
    padding: 15px 30px;
    background-color: $blue-light;

    .icon {
      width: 14px;
      height: 14px;
      margin-right: 6px;
      color: #466af0;
    }

    .warning {
      &__highlight {
        margin-right: 3px;
        color: #466af0;
        font-weight: font-weight('weight-bold');
        font-size: font-size('size-medium');
      }

      &--text {
        font-size: font-size('size-medium');
      }
    }
  }

  &--body {
    height: auto;
  }

  &--close-button {
    position: absolute;
    top: 0;
    right: 0;
    padding: 15px;
    background: transparent;
    color: $white;
    border: none;
  }

  &--header,
  &--footer {
    padding-left: 30px;
    padding-right: 30px;
  }

  &--header {
    position: sticky;
    top: 0;
    padding-top: 15px;
    padding-bottom: 25px;
    z-index: 1;
    color: $white;
    background-color: $blue;

    &.hasSubtitle {
      padding-top: 35px;
    }

    &__small {
      svg.icon {
        width: 12px;
        height: 12px;
      }
    }
  }

  &--footer {
    position: sticky;
    padding-top: 1px;
    bottom: 0;
    z-index: 1;
    background-color: $white;
    .flex-buttons {
      display: flex;
      justify-content: space-between;
      padding: 15px 0;
    }
  }
}
</style>
